import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { CheckCircle } from 'lucide-react';

interface CompleteAppointmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointment: {
    id: string;
    name: string;
    service_type: string;
  } | null;
  onComplete?: () => void;
}

export default function CompleteAppointmentDialog({
  open,
  onOpenChange,
  appointment,
  onComplete,
}: CompleteAppointmentDialogProps) {
  const { toast } = useToast();
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleComplete = async () => {
    if (!appointment) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('appointments')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          completion_notes: notes || null,
        })
        .eq('id', appointment.id);

      if (error) throw error;

      toast({
        title: 'Appointment Completed',
        description: 'The appointment has been marked as completed.',
      });

      setNotes('');
      onOpenChange(false);
      onComplete?.();
    } catch (error) {
      console.error('Error completing appointment:', error);
      toast({
        title: 'Error',
        description: 'Failed to mark appointment as completed.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            Complete Appointment
          </DialogTitle>
          <DialogDescription>
            Mark this appointment as completed and optionally add notes.
          </DialogDescription>
        </DialogHeader>
        
        {appointment && (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="text-sm">
                <span className="font-semibold">Client:</span> {appointment.name}
              </div>
              <div className="text-sm">
                <span className="font-semibold">Service:</span> {appointment.service_type}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="completion-notes">Completion Notes (Optional)</Label>
              <Textarea
                id="completion-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about the appointment..."
                rows={4}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleComplete}
            disabled={isSubmitting}
            className="bg-green-600 hover:bg-green-700"
          >
            {isSubmitting ? 'Completing...' : 'Mark Complete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
